/**
 * Tasks List Component
 * Manages and displays user tasks with CRUD operations
 */

import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../utils/api';
import './TasksList.css';

const TasksList = () => {
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch tasks from API
   */
  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet('/api/tasks');
      
      // Handle the API response structure
      const tasksData = response.data || response;
      setTasks(tasksData);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setError('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Add new task
   * @param {Event} e - Form submit event
   */
  const handleAddTask = async (e) => {
    e.preventDefault();
    
    if (!newTask.trim()) return;

    try {
      const taskData = {
        name: newTask.trim(),
        completed: false,
        priority: 'medium'
      };

      const response = await apiPost('/api/tasks', taskData);
      const newTaskData = response.data || response;
      setTasks(prev => [...prev, newTaskData]);
      setNewTask('');
    } catch (error) {
      console.error('Error adding task:', error);
      alert('Failed to add task');
    }
  };

  /**
   * Toggle task completion status
   * @param {string} id - Task ID
   * @param {boolean} completed - Current completion status
   */
  const handleToggleTask = async (id, completed) => {
    try {
      const response = await apiPatch(`/api/tasks/${id}`, { completed: !completed });
      const updatedTask = response.data || response;
      setTasks(prev => prev.map(task => 
        task._id === id ? updatedTask : task
      ));
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Failed to update task');
    }
  };

  /**
   * Delete task
   * @param {string} id - Task ID
   */
  const handleDeleteTask = async (id) => {
    try {
      await apiDelete(`/api/tasks/${id}`);
      setTasks(prev => prev.filter(task => task._id !== id));
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task');
    }
  };

  /**
   * Update task priority
   * @param {string} id - Task ID
   * @param {string} priority - New priority level
   */
  const handleUpdatePriority = async (id, priority) => {
    try {
      const response = await apiPatch(`/api/tasks/${id}`, { priority });
      const updatedTask = response.data || response;
      setTasks(prev => prev.map(task => 
        task._id === id ? updatedTask : task
      ));
    } catch (error) {
      console.error('Error updating task priority:', error);
      alert('Failed to update task priority');
    }
  };

  // Fetch tasks on component mount
  useEffect(() => {
    fetchTasks();
  }, []);

  if (loading) {
    return (
      <div className="tasks-loading">
        <p>Loading tasks...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tasks-error">
        <p>{error}</p>
        <button onClick={fetchTasks} className="retry-button">
          Try Again
        </button>
      </div>
    );
  }

  // Sort tasks by priority and completion status
  const sortedTasks = [...tasks].sort((a, b) => {
    // Completed tasks go to bottom
    if (a.completed !== b.completed) {
      return a.completed ? 1 : -1;
    }
    
    // Sort by priority: high > medium > low
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  });

  return (
    <div className="tasks-container">
      {/* Add Task Form */}
      <form onSubmit={handleAddTask} className="add-task-form">
        <input
          type="text"
          value={newTask}
          onChange={(e) => setNewTask(e.target.value)}
          placeholder="Add a new task..."
          className="task-input"
        />
        <button type="submit" className="add-task-button">
          Add Task
        </button>
      </form>

      {/* Tasks List */}
      <div className="tasks-list">
        {sortedTasks.length === 0 ? (
          <p className="no-tasks">No tasks yet. Add one above!</p>
        ) : (
          sortedTasks.map(task => (
            <div 
              key={task._id} 
              className={`task-item ${task.completed ? 'completed' : ''}`}
            >
              <div className="task-content">
                <input
                  type="checkbox"
                  checked={task.completed}
                  onChange={() => handleToggleTask(task._id, task.completed)}
                  className="task-checkbox"
                />
                <span className="task-name">{task.name}</span>
                
                <select
                  value={task.priority}
                  onChange={(e) => handleUpdatePriority(task._id, e.target.value)}
                  className={`priority-select priority-${task.priority}`}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              
              <button
                onClick={() => handleDeleteTask(task._id)}
                className="delete-task-button"
                title="Delete task"
              >
                ×
              </button>
            </div>
          ))
        )}
      </div>

      {/* Task Statistics */}
      {tasks.length > 0 && (
        <div className="task-stats">
          <span>Total: {tasks.length}</span>
          <span>Completed: {tasks.filter(t => t.completed).length}</span>
          <span>Pending: {tasks.filter(t => !t.completed).length}</span>
        </div>
      )}
    </div>
  );
};

export default TasksList;